import * as THREE from 'three';
import { INTERACTION } from './systems/balance.js';

// ============================================================================
// INTERACTION SYSTEM
// ============================================================================
// Pointer event handling, raycasting with layers architecture, bounding
// volumes, and interactable registry. Foundation for all click/tap
// interactions in the game.

// Raycaster dedicated to interaction — tests only layer 1 (bounding volumes)
const raycaster = new THREE.Raycaster();
raycaster.layers.set(1);

// Normalized device coordinates for raycasting
const pointer = new THREE.Vector2();

// WeakMap for O(1) lookup: bounding mesh -> interactable config
const meshToInteractable = new WeakMap();

// All registered interactables
const interactables = [];

// Drag detection state
let pointerDownPos = { x: 0, y: 0 };
let pointerDownType = 'mouse';
let isDragging = false;

// Hover state
let hoverFrameCount = 0;
let currentHovered = null;

// Whether the device supports hover (non-touch primary input)
let _hasHoverCapability = false;

// Warm emissive color used when a material has no emissive set (black)
const HOVER_EMISSIVE_COLOR = new THREE.Color(0x443322);
const HOVER_EMISSIVE_INTENSITY = 0.15;

// Module-level references set during init
let _renderer = null;
let _camera = null;
let _scene = null;

/**
 * Initialize the interaction system.
 * Attaches pointer event listeners to the renderer's canvas.
 */
export function initInteractionSystem(renderer, camera, scene) {
  _renderer = renderer;
  _camera = camera;
  _scene = scene;

  // Detect hover capability once at init (false on touch-only devices)
  _hasHoverCapability = window.matchMedia('(hover: hover)').matches;

  const canvas = renderer.domElement;

  // --- pointerdown: record start position and input type ---
  canvas.addEventListener('pointerdown', (e) => {
    pointerDownPos = { x: e.clientX, y: e.clientY };
    pointerDownType = e.pointerType;
    isDragging = false;
  });

  // --- pointermove: detect drag and update pointer for hover ---
  canvas.addEventListener('pointermove', (e) => {
    // Update drag detection
    const dx = e.clientX - pointerDownPos.x;
    const dy = e.clientY - pointerDownPos.y;
    const threshold = pointerDownType === 'touch' ? INTERACTION.DRAG_THRESHOLD_TOUCH : INTERACTION.DRAG_THRESHOLD_MOUSE;
    if (Math.sqrt(dx * dx + dy * dy) > threshold) {
      isDragging = true;
    }

    // Update pointer coordinates for hover raycasting
    pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
    pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
  });

  // --- pointerup: fire click if not a drag ---
  canvas.addEventListener('pointerup', (e) => {
    if (isDragging) return;
    // Do NOT call e.stopPropagation() or e.preventDefault()

    pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
    pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;

    raycaster.setFromCamera(pointer, _camera);

    const boundingMeshes = interactables.map(i => i.boundingMesh);
    const intersects = raycaster.intersectObjects(boundingMeshes, false);

    if (intersects.length > 0) {
      const interactable = meshToInteractable.get(intersects[0].object);
      if (interactable) {
        const now = Date.now();
        if (now - interactable.lastClickTime > interactable.cooldown) {
          interactable.lastClickTime = now;
          interactable.onClick();
        }
      }
    }
  });
}

/**
 * Register an object as interactable.
 *
 * @param {THREE.Object3D} mesh - The visible mesh/group to make interactive
 * @param {object} config
 * @param {string} config.type - 'creature' | 'object' | 'secret'
 * @param {string} config.id - Unique identifier for discovery tracking
 * @param {function} config.onClick - Click handler
 * @param {function} [config.onHover] - Hover start handler
 * @param {function} [config.onHoverEnd] - Hover end handler
 * @param {number} [config.cooldown=500] - ms between allowed clicks
 * @param {number} config.boundingRadius - Radius for bounding sphere
 */
export function registerInteractable(mesh, config) {
  const boundingRadius = config.boundingRadius || 1.5;
  const cooldown = config.cooldown ?? (config.type === 'creature' ? 500 : 1000);

  // Create invisible bounding sphere on layer 1
  const boundingGeometry = new THREE.SphereGeometry(boundingRadius);
  const boundingMaterial = new THREE.MeshBasicMaterial({ visible: false });
  const boundingMesh = new THREE.Mesh(boundingGeometry, boundingMaterial);
  boundingMesh.layers.set(1);

  // Add as child of the target mesh so it follows automatically
  mesh.add(boundingMesh);

  const interactable = {
    mesh,
    boundingMesh,
    type: config.type,
    id: config.id,
    onClick: config.onClick,
    onHover: config.onHover || null,
    onHoverEnd: config.onHoverEnd || null,
    cooldown,
    lastClickTime: 0,
  };

  meshToInteractable.set(boundingMesh, interactable);
  interactables.push(interactable);

  return interactable;
}

/**
 * Unregister an interactable, removing its bounding mesh and entry from the registry.
 *
 * @param {THREE.Object3D} mesh - The mesh that was registered
 */
export function unregisterInteractable(mesh) {
  const idx = interactables.findIndex(i => i.mesh === mesh);
  if (idx === -1) return;

  const interactable = interactables[idx];

  // Remove bounding mesh from its parent
  if (interactable.boundingMesh.parent) {
    interactable.boundingMesh.parent.remove(interactable.boundingMesh);
  }
  interactable.boundingMesh.geometry.dispose();
  interactable.boundingMesh.material.dispose();

  // Remove from WeakMap and array
  meshToInteractable.delete(interactable.boundingMesh);
  interactables.splice(idx, 1);

  // Clear hover state if this was the hovered interactable
  if (currentHovered === interactable) {
    currentHovered = null;
    if (_renderer) _renderer.domElement.style.cursor = 'default';
  }
}

/**
 * Get the current pointer NDC coordinates.
 * @returns {THREE.Vector2} The pointer NDC
 */
export function getPointerNDC() {
  return pointer;
}

/**
 * Update the mesh reference for an interactable (e.g. when GLB replaces procedural).
 * Moves the bounding sphere from the old mesh to the new one.
 *
 * @param {string} id - The interactable's id
 * @param {THREE.Object3D} newMesh - The new mesh to attach to
 */
export function updateInteractableMesh(id, newMesh) {
  const interactable = interactables.find(i => i.id === id);
  if (!interactable) return;

  // Remove bounding mesh from old parent
  if (interactable.boundingMesh.parent) {
    interactable.boundingMesh.parent.remove(interactable.boundingMesh);
  }

  // Add to new parent
  newMesh.add(interactable.boundingMesh);
  interactable.mesh = newMesh;
}

/**
 * Apply emissive highlight to all child meshes of an interactable's mesh.
 * Stores original values in userData so they can be restored later.
 *
 * @param {THREE.Object3D} object - The interactable's mesh (may be Group, Mesh, etc.)
 */
function applyEmissiveHighlight(object) {
  object.traverse((child) => {
    if (!child.isMesh || !child.material) return;

    const mat = child.material;

    // Skip if this mesh already has highlight stored (e.g. shared traversal)
    if (child.userData._origEmissiveIntensity !== undefined) return;

    // Store originals
    child.userData._origEmissiveIntensity = mat.emissiveIntensity;
    child.userData._origEmissive = mat.emissive ? mat.emissive.getHex() : null;

    // Only modify materials that support emissive (Standard, Physical, Lambert, Phong)
    if (mat.emissive === undefined) return;

    // If emissive is black (no emissive set), apply a warm color
    if (mat.emissive.getHex() === 0x000000) {
      mat.emissive.setHex(HOVER_EMISSIVE_COLOR.getHex());
      child.userData._didSetEmissiveColor = true;
    }

    // Boost emissive intensity to at least the hover threshold
    mat.emissiveIntensity = Math.max(mat.emissiveIntensity, HOVER_EMISSIVE_INTENSITY);
  });
}

/**
 * Remove emissive highlight from all child meshes, restoring original values.
 *
 * @param {THREE.Object3D} object - The interactable's mesh (may be Group, Mesh, etc.)
 */
function removeEmissiveHighlight(object) {
  object.traverse((child) => {
    if (!child.isMesh || !child.material) return;

    // Only restore if we previously stored values
    if (child.userData._origEmissiveIntensity === undefined) return;

    const mat = child.material;

    // Restore emissive intensity
    mat.emissiveIntensity = child.userData._origEmissiveIntensity;

    // Restore emissive color if we changed it
    if (child.userData._didSetEmissiveColor && mat.emissive !== undefined) {
      mat.emissive.setHex(child.userData._origEmissive !== null ? child.userData._origEmissive : 0x000000);
    }

    // Clean up stored data
    delete child.userData._origEmissiveIntensity;
    delete child.userData._origEmissive;
    delete child.userData._didSetEmissiveColor;
  });
}

/**
 * Call once per frame in the animation loop.
 * Handles throttled hover raycasting.
 */
export function updateInteraction() {
  if (!_renderer || !_camera) return;

  // Throttled hover detection — every 3rd frame
  hoverFrameCount++;
  if (hoverFrameCount % 3 !== 0) return;

  raycaster.setFromCamera(pointer, _camera);

  const boundingMeshes = interactables.map(i => i.boundingMesh);
  const intersects = raycaster.intersectObjects(boundingMeshes, false);

  let newHovered = null;
  if (intersects.length > 0) {
    newHovered = meshToInteractable.get(intersects[0].object);
  }

  if (newHovered !== currentHovered) {
    // --- End hover on previous interactable ---
    if (currentHovered) {
      // Remove emissive highlight (only on hover-capable devices)
      if (_hasHoverCapability) {
        removeEmissiveHighlight(currentHovered.mesh);
      }
      // Fire custom callback if defined
      if (currentHovered.onHoverEnd) {
        currentHovered.onHoverEnd();
      }
    }

    // --- Start hover on new interactable ---
    if (newHovered) {
      // Apply emissive highlight (only on hover-capable devices)
      if (_hasHoverCapability) {
        applyEmissiveHighlight(newHovered.mesh);
      }
      // Fire custom callback if defined
      if (newHovered.onHover) {
        newHovered.onHover();
      }
    }

    currentHovered = newHovered;
    _renderer.domElement.style.cursor = newHovered ? 'pointer' : 'default';
  }
}
